import { useCallback, useEffect, useState } from "react"
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import CheckRoundedIcon from "@mui/icons-material/CheckRounded"
import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import { ConsultantClient } from "@clients/consultantClient"
import { LeadRoutingClient } from "@clients/leadRoutingClient"
import { Rule } from "@features/lead-routing/leadRouting.types"

type TagCount = { tag: string; consultantCount: number }

// The tag catalog. Rename and delete are deliberately separate actions: rename
// (the pencil) changes the label everywhere; delete (the trash) removes the tag
// from every consultant and rule, and always confirms first with its impact.
export function TagManagerDialog(props: {
  open: boolean
  onClose: () => void
}) {
  const [tags, setTags] = useState<TagCount[]>([])
  const [rules, setRules] = useState<Rule[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState("")
  const [toDelete, setToDelete] = useState<TagCount | null>(null)
  const [newTag, setNewTag] = useState("")
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([
      ConsultantClient.listTagCounts(),
      LeadRoutingClient.listRules().catch(() => [] as Rule[]),
    ])
      .then(([tagCounts, ruleList]) => {
        setTags(tagCounts)
        setRules(ruleList)
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (props.open) {
      load()
      setEditing(null)
      setToDelete(null)
    }
  }, [props.open, load])

  const rulesUsingTag = (tag: string) =>
    rules.filter((rule) =>
      (rule.requiredTagConditions ?? []).some((condition) =>
        condition.tags.includes(tag),
      ),
    )

  const createTag = async () => {
    const name = newTag.trim().toLowerCase()
    if (!name) {
      return
    }
    setBusy(true)
    try {
      await ConsultantClient.createTag(name)
      setNewTag("")
      load()
    } finally {
      setBusy(false)
    }
  }

  const startRename = (tag: string) => {
    setEditing(tag)
    setDraft(tag)
  }

  const saveRename = async (from: string) => {
    const to = draft.trim().toLowerCase()
    if (!to || to === from) {
      setEditing(null)
      return
    }
    setBusy(true)
    try {
      await ConsultantClient.renameTag(from, to)
      await Promise.all(
        rulesUsingTag(from).map((rule) =>
          LeadRoutingClient.updateRule(rule.id, {
            requiredTagConditions: (rule.requiredTagConditions ?? []).map(
              (condition) => ({
                ...condition,
                tags: Array.from(
                  new Set(condition.tags.map((t) => (t === from ? to : t))),
                ),
              }),
            ),
          }),
        ),
      )
      setEditing(null)
      load()
    } finally {
      setBusy(false)
    }
  }

  const confirmDelete = async () => {
    if (!toDelete) {
      return
    }
    const tag = toDelete.tag
    setBusy(true)
    try {
      await ConsultantClient.deleteTag(tag)
      await Promise.all(
        rulesUsingTag(tag).map((rule) =>
          LeadRoutingClient.updateRule(rule.id, {
            // Drop the tag from each condition, then drop conditions left empty.
            requiredTagConditions: (rule.requiredTagConditions ?? [])
              .map((condition) => ({
                ...condition,
                tags: condition.tags.filter((t) => t !== tag),
              }))
              .filter((condition) => condition.tags.length > 0),
          }),
        ),
      )
      setToDelete(null)
      load()
    } finally {
      setBusy(false)
    }
  }

  const deleteRuleCount = toDelete ? rulesUsingTag(toDelete.tag).length : 0

  return (
    <>
      <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Tags</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Create a tag so it is ready to use, rename it to change its label
            everywhere, or delete it to remove it from every consultant and
            routing rule. Removing a tag from a consultant on the table does not
            delete it here.
          </Typography>
          <Stack direction="row" spacing={1} mb={2}>
            <TextField
              size="small"
              placeholder="New tag"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") createTag()
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              disabled={busy || newTag.trim().length === 0}
              onClick={createTag}
            >
              Add tag
            </Button>
          </Stack>
          {loading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          ) : tags.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2}>
              No tags yet. Add a tag to a consultant on the table and it shows
              up here.
            </Typography>
          ) : (
            <Stack
              divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}
            >
              {tags.map((entry) => {
                const ruleCount = rulesUsingTag(entry.tag).length
                const isEditing = editing === entry.tag
                return (
                  <Stack
                    key={entry.tag}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ py: 1 }}
                  >
                    {isEditing ? (
                      <TextField
                        size="small"
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveRename(entry.tag)
                          if (e.key === "Escape") setEditing(null)
                        }}
                        sx={{ flex: 1 }}
                      />
                    ) : (
                      <>
                        <Chip
                          label={entry.tag}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {entry.consultantCount} consultant
                          {entry.consultantCount === 1 ? "" : "s"}
                          {ruleCount > 0
                            ? ` · ${ruleCount} rule${
                                ruleCount === 1 ? "" : "s"
                              }`
                            : ""}
                        </Typography>
                        <Box flex={1} />
                      </>
                    )}
                    {isEditing ? (
                      <>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={busy}
                          onClick={() => saveRename(entry.tag)}
                          aria-label="save"
                        >
                          <CheckRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={busy}
                          onClick={() => setEditing(null)}
                          aria-label="cancel"
                        >
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <Stack direction="row" sx={{ color: "text.secondary" }}>
                        <Tooltip title="Rename">
                          <IconButton
                            size="small"
                            onClick={() => startRename(entry.tag)}
                            aria-label="rename tag"
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setToDelete(entry)}
                            aria-label="delete tag"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                )
              })}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!toDelete} onClose={() => setToDelete(null)} maxWidth="xs">
        <DialogTitle>Delete “{toDelete?.tag}”?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the tag from {toDelete?.consultantCount} consultant
            {toDelete?.consultantCount === 1 ? "" : "s"}
            {deleteRuleCount > 0
              ? ` and drops it from ${deleteRuleCount} routing rule${
                  deleteRuleCount === 1 ? "" : "s"
                }`
              : ""}
            . This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={busy}
          >
            Delete tag
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
